// Минимальная fetch-обёртка. Использует cookie-credentials для
// сессии, JSON для тела, бросает ApiError на не-2xx с разобранным
// JSON-ответом сервера.

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly issues?: unknown;

  constructor(status: number, message: string, code?: string, issues?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.issues = issues;
  }
}

interface RequestOptions<TBody> {
  method?: string;
  body?: TBody;
  query?: Record<string, string | number | boolean | undefined>;
}

// CSRF: backend (SPEC §10) на /auth/totp success ставит non-httpOnly
// cookie tracker_csrf. На каждом небезопасном запросе достаём её и
// кладём в заголовок X-CSRF-Token. Если cookie нет (не залогинен
// или logout уже почистил) — header не добавляем; запрос всё равно
// будет отбит sessionGuard'ом или csrfGuard'ом на бэкенде.
const CSRF_COOKIE_NAME = 'tracker_csrf';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function getCsrfTokenFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const target = `${CSRF_COOKIE_NAME}=`;
  const match = document.cookie.split('; ').find((c) => c.startsWith(target));
  return match ? decodeURIComponent(match.slice(target.length)) : null;
}

async function request<TResponse, TBody = unknown>(
  url: string,
  opts: RequestOptions<TBody> = {},
): Promise<TResponse> {
  const finalUrl = opts.query
    ? `${url}?${new URLSearchParams(filterUndefined(opts.query)).toString()}`
    : url;
  const method = (opts.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = {};
  if (opts.body) headers['content-type'] = 'application/json';
  if (!SAFE_METHODS.has(method)) {
    const csrf = getCsrfTokenFromCookie();
    if (csrf) headers['x-csrf-token'] = csrf;
  }
  const res = await fetch(finalUrl, {
    method,
    credentials: 'include',
    headers: Object.keys(headers).length > 0 ? headers : undefined,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    let payload: { error?: string; message?: string; issues?: unknown } = {};
    try {
      payload = (await res.json()) as typeof payload;
    } catch {
      // body не JSON — оставим payload пустым
    }
    throw new ApiError(
      res.status,
      payload.message ?? payload.error ?? `HTTP ${res.status}`,
      payload.error,
      payload.issues,
    );
  }
  if (res.status === 204) return undefined as TResponse;
  return (await res.json()) as TResponse;
}

function filterUndefined(
  q: Record<string, string | number | boolean | undefined>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined) out[k] = String(v);
  }
  return out;
}

async function uploadFile<TResponse>(url: string, formData: FormData): Promise<TResponse> {
  // Multipart — отдельно от json-обёртки: не выставляем content-type
  // (браузер сам выставит multipart/form-data с правильным boundary).
  // CSRF-токен берём из cookie тем же способом, что и в request().
  const headers: Record<string, string> = {};
  const csrf = getCsrfTokenFromCookie();
  if (csrf) headers['x-csrf-token'] = csrf;
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });
  if (!res.ok) {
    let payload: { error?: string; message?: string; issues?: unknown } = {};
    try {
      payload = (await res.json()) as typeof payload;
    } catch {
      // body не JSON
    }
    throw new ApiError(
      res.status,
      payload.message ?? payload.error ?? `HTTP ${res.status}`,
      payload.error,
      payload.issues,
    );
  }
  return (await res.json()) as TResponse;
}

export const api = {
  get: <T>(url: string, query?: RequestOptions<never>['query']): Promise<T> =>
    request<T>(url, { method: 'GET', query }),
  post: <T, B = unknown>(url: string, body?: B): Promise<T> =>
    request<T, B>(url, { method: 'POST', body }),
  patch: <T, B = unknown>(url: string, body?: B): Promise<T> =>
    request<T, B>(url, { method: 'PATCH', body }),
  delete: <T>(url: string): Promise<T> => request<T>(url, { method: 'DELETE' }),
  upload: uploadFile,
};
